/* eslint-disable react-refresh/only-export-components -- Context modules intentionally export provider + hooks. */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { Role, Permissions, parsePermissions } from '../types/permissions';

interface PermissionsContextType {
  activeRole: Role | null;
  permissions: Permissions;
  availableRoles: Role[];
  setActiveRole: (role: Role) => void;
  isLoading: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
);

interface PermissionsProviderProps {
  children: React.ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { user } = useAuth();
  const [activeRole, setActiveRoleState] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permissions>({});
  const [isLoading, setIsLoading] = useState(true);

  // Get available roles from user
  const availableRoles = useMemo(() => user?.roles || [], [user?.roles]);

  // Load active role from localStorage or set default
  useEffect(() => {
    if (!user || !availableRoles.length) {
      setActiveRoleState(null);
      setPermissions({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      // Try to get saved role from localStorage
      const savedRoleId = localStorage.getItem(`activeRole_${user.id}`);
      let roleToSet: Role | null = null;

      if (savedRoleId) {
        // Find saved role in available roles
        roleToSet =
          availableRoles.find(
            (role: Role) => role.id.toString() === savedRoleId
          ) || null;
      }

      // If no saved role or saved role not found, use first available role
      if (!roleToSet && availableRoles.length > 0) {
        roleToSet = availableRoles[0];
      }

      setActiveRoleState(roleToSet);

      // Parse permissions from active role
      if (roleToSet) {
        const parsedPermissions = parsePermissions(roleToSet.json_permissions);
        setPermissions(parsedPermissions);
      } else {
        setPermissions({});
      }
    } catch {
      // Fallback to first available role
      if (availableRoles.length > 0) {
        const fallbackRole = availableRoles[0];
        setActiveRoleState(fallbackRole);
        setPermissions(parsePermissions(fallbackRole.json_permissions));
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, availableRoles]);

  const setActiveRole = (role: Role) => {
    setActiveRoleState(role);
    setPermissions(parsePermissions(role.json_permissions));

    // Save to localStorage
    if (user) {
      localStorage.setItem(`activeRole_${user.id}`, role.id.toString());
    }
  };

  const value: PermissionsContextType = {
    activeRole,
    permissions,
    availableRoles,
    setActiveRole,
    isLoading,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
